import { format } from 'date-fns';
import { UserIcon, GlobeIcon, LinkedinIcon, GithubIcon } from 'lucide-react';
import type { Customer } from '@repo/types';
import { SectionCard, EmptyState, LinkChip, DescriptionList } from './_shared';
import { humanize } from './utils';

const fmtDate = (v?: string | Date | null) => (v ? format(new Date(v), 'MMM dd, yyyy') : null);

/**
 * Personal details, summary and links — limited to what customers provide
 * themselves via the web app's Profile settings (Personal + Skills & Links
 * sections). Fields that only exist in the backend schema are deliberately
 * not surfaced here.
 */
export function CustomerOverviewTab({ customer }: { customer: Customer }) {
  const personal = ([
    ['Gender', humanize(customer.gender)],
    ['Date of Birth', fmtDate(customer.dob)],
    ['WhatsApp', customer.whatsappPhone],
    ['Street Address', customer.streetAddress],
    ['City', customer.city],
    ['State', customer.state],
    ['Country', customer.country],
    ['Pincode', customer.pincode],
  ] as Array<[string, string | null | undefined]>).filter(([, v]) => Boolean(v));

  const hasLinks = customer.linkedinUrl || customer.githubUrl || customer.personalWebsite;

  return (
    <div className="space-y-5">
      <SectionCard icon={UserIcon} title="Personal Details">
        {personal.length > 0 ? (
          <DescriptionList items={personal.map(([label, value]) => ({ label, value }))} />
        ) : (
          <EmptyState icon={UserIcon} message="No personal details on file" />
        )}
      </SectionCard>

      <SectionCard icon={GlobeIcon} title="Online Presence">
        {hasLinks ? (
          <div className="flex flex-wrap gap-2.5">
            {customer.linkedinUrl && <LinkChip href={customer.linkedinUrl} icon={LinkedinIcon} label="LinkedIn" />}
            {customer.githubUrl && <LinkChip href={customer.githubUrl} icon={GithubIcon} label="GitHub" />}
            {customer.personalWebsite && <LinkChip href={customer.personalWebsite} icon={GlobeIcon} label="Website" />}
          </div>
        ) : (
          <EmptyState icon={GlobeIcon} message="No links provided" />
        )}
      </SectionCard>
    </div>
  );
}
